import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBackRounded, AutoAwesomeRounded, CheckCircleRounded } from '@mui/icons-material';
import {
  buildCcdDraft,
  computeTcu,
  CAP_LANGUAGE_CODES,
  TMAI_CAP_FRAMEWORK_2022_03,
  type AcademicLevel,
  type CcdDraft,
  type CourseCcdData,
  type CourseTemplateProps,
  type SubjectAreaCode,
  type TimeAllocation,
} from '@dosfilos/domain';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { usePermissions } from '../../hooks/use-permissions';
import { capApi } from '../../lib/api/cap.api';
import { errorText } from '../../lib/error-text';

// ─── CCD editor (Phase E — CAP, PR2) ────────────────────────────────────────
// Wireframe screen 2: TMAI Course Catalog Description over a CourseTemplate.
// The AI-derived draft (buildCcdDraft) is a PROPOSAL — applied only when the
// user clicks the banner action; confirming calls capUpsertCcd, which
// re-validates and freezes the per-level TCU server-side.

const LEVELS: AcademicLevel[] = ['undergraduate', 'graduate'];

const SUBJECT_AREAS: SubjectAreaCode[] = [
  'OT_SURVEY',
  'NT_SURVEY',
  'HERMENEUTICS',
  'THEOLOGY',
  'TEXTUAL_EXEGESIS',
  'EXPOSITORY_PREACHING',
  'PASTORAL_MINISTRIES',
  'ELECTIVES',
];

const DELIVERY_FORMATS = [
  'residential',
  'modular_onsite',
  'modular_blended',
  'online_independent',
  'online_with_sessions',
] as const;

const ALLOCATION_ROWS: (keyof TimeAllocation)[] = [
  'lecture',
  'reading',
  'homework',
  'projects',
  'exams',
];

const EMPTY_ALLOCATION: TimeAllocation = {
  lecture: 0,
  reading: 0,
  homework: 0,
  projects: 0,
  exams: 0,
};

interface CcdFormState {
  courseNumber: string;
  capSubjectArea: SubjectAreaCode | '';
  academicLevels: AcademicLevel[];
  timeAllocation: Record<AcademicLevel, TimeAllocation>;
  tcu: Record<AcademicLevel, string>; // text inputs; parsed on confirm
  description: string;
  deliveryFormat: string;
  targetAudience: string;
  targetAudienceLanguages: string[]; // ISO 639-3 codes
  instructionalLanguage: string; // ISO 639-3 code (or '')
  translationProvided: '' | 'yes' | 'no'; // 'na' derived, never picked here
  externalAccreditation: string;
  closUndergraduate: string; // one CLO per line
  closGraduate: string;
}

function emptyForm(): CcdFormState {
  return {
    courseNumber: '',
    capSubjectArea: '',
    academicLevels: ['undergraduate'],
    timeAllocation: { undergraduate: { ...EMPTY_ALLOCATION }, graduate: { ...EMPTY_ALLOCATION } },
    tcu: { undergraduate: '', graduate: '' },
    description: '',
    deliveryFormat: '',
    targetAudience: '',
    targetAudienceLanguages: [],
    instructionalLanguage: '',
    translationProvided: '',
    externalAccreditation: '',
    closUndergraduate: '',
    closGraduate: '',
  };
}

function formFromCap(cap: CourseCcdData): CcdFormState {
  const base = emptyForm();
  return {
    ...base,
    courseNumber: cap.courseNumber ?? '',
    capSubjectArea: cap.capSubjectArea ?? '',
    academicLevels: cap.academicLevels.length > 0 ? [...cap.academicLevels] : ['undergraduate'],
    timeAllocation: {
      undergraduate: { ...EMPTY_ALLOCATION, ...(cap.timeAllocation.undergraduate ?? {}) },
      graduate: { ...EMPTY_ALLOCATION, ...(cap.timeAllocation.graduate ?? {}) },
    },
    tcu: {
      undergraduate: cap.tcu.undergraduate != null ? String(cap.tcu.undergraduate) : '',
      graduate: cap.tcu.graduate != null ? String(cap.tcu.graduate) : '',
    },
    description: cap.description ?? '',
    deliveryFormat: cap.deliveryFormat ?? '',
    targetAudience: cap.targetAudience ?? '',
    targetAudienceLanguages: cap.targetAudienceLanguages ? [...cap.targetAudienceLanguages] : [],
    instructionalLanguage: cap.instructionalLanguage ?? '',
    translationProvided: cap.translationProvided === 'na' ? '' : (cap.translationProvided ?? ''),
    externalAccreditation: cap.externalAccreditation ?? '',
    closUndergraduate: (cap.clos?.undergraduate ?? []).join('\n'),
    closGraduate: (cap.clos?.graduate ?? []).join('\n'),
  };
}

function applyDraft(form: CcdFormState, draft: CcdDraft): CcdFormState {
  return {
    ...form,
    courseNumber: form.courseNumber || (draft.courseNumber ?? ''),
    description: form.description || (draft.description ?? ''),
    closUndergraduate: form.closUndergraduate || draft.clos.undergraduate.join('\n'),
    closGraduate: form.closGraduate || draft.clos.graduate.join('\n'),
    timeAllocation: {
      ...form.timeAllocation,
      ...(draft.timeAllocation.undergraduate &&
      ALLOCATION_ROWS.every((k) => form.timeAllocation.undergraduate[k] === 0)
        ? { undergraduate: { ...draft.timeAllocation.undergraduate } }
        : {}),
    },
  };
}

const basename = (path: string): string => path.split('/').pop() ?? path;

export function CcdEditorPage(): JSX.Element {
  const { t } = useTranslation('cap');
  const { isAdmin, hasRole } = usePermissions();
  const navigate = useNavigate();
  // ─── La ficha es DEL CURSO, y ahora se entra por el curso ─────────────────
  //
  // La ficha vive en `courseCcds/{courseId}` desde #422, pero la única puerta de
  // entrada era una PLANTILLA DE SYLLABUS: el editor cargaba la plantilla para
  // sacarle el `courseId`. Un curso sin plantilla no tenía forma de recibir su
  // ficha, aunque el dato la admitiera perfectamente — y eso bloqueaba el
  // trabajo real de SEBEX, donde los CCD se escriben curso por curso mucho antes
  // de que exista un syllabus.
  //
  // La plantilla no desaparece: cuando el curso tiene una, sigue proponiendo el
  // borrador (`buildCcdDraft`). Pasa de ser el camino obligatorio a ser una
  // ayuda cuando está disponible.
  const { courseId: courseIdParam, courseTemplateId } = useParams<{
    courseId: string;
    courseTemplateId: string;
  }>();
  const { currentTenantId } = useTenantStore();

  const [course, setCourse] = useState<{ id: string; title: string } | null>(null);
  const [form, setForm] = useState<CcdFormState>(emptyForm());
  /** The card as stored — drives the "already confirmed" cues (frozen TCU chip,
   *  prefill banner). Separate from `form`, which is what the user is editing. */
  const [stored, setStored] = useState<CourseCcdData | null>(null);
  const [draft, setDraft] = useState<CcdDraft | null>(null);
  const [draftApplied, setDraftApplied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedVersion, setSavedVersion] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      if (!currentTenantId || (!courseIdParam && !courseTemplateId)) return;
      setLoading(true);
      setError(null);
      try {
        // Dos puertas, un destino. La ruta vieja (por plantilla) se conserva
        // para no romper enlaces guardados: resuelve el curso y sigue igual.
        let courseId = courseIdParam ?? '';
        if (!courseId && courseTemplateId) {
          const snap = await getDoc(
            doc(db, `tenants/${currentTenantId}/courseTemplates/${courseTemplateId}`),
          );
          if (!snap.exists()) throw new Error(t('ccd.errors.templateNotFound'));
          const data = snap.data() as unknown as CourseTemplateProps;
          // Sin curso no hay dónde poner la ficha: el id del documento ES el id
          // del curso. Se dice en voz alta en vez de fallar al guardar.
          if (!data.courseId) throw new Error(t('ccd.errors.templateNotLinked'));
          courseId = data.courseId;
        }

        const [courseSnap, ccdSnap, tplSnap] = await Promise.all([
          getDoc(doc(db, `tenants/${currentTenantId}/courses/${courseId}`)),
          getDoc(doc(db, `tenants/${currentTenantId}/courseCcds/${courseId}`)),
          // La plantilla ya no manda, pero si existe sigue proponiendo el
          // borrador. Un curso sin plantilla simplemente empieza en blanco.
          getDocs(
            query(
              collection(db, `tenants/${currentTenantId}/courseTemplates`),
              where('courseId', '==', courseId),
            ),
          ),
        ]);
        if (cancelled) return;
        if (!courseSnap.exists()) throw new Error(t('ccd.errors.courseNotFound'));
        const courseData = courseSnap.data() as { name?: string; code?: string };
        const stored = ccdSnap.exists()
          ? ((ccdSnap.data() as { data?: CourseCcdData }).data ?? null)
          : null;
        const tpl = tplSnap.docs.find((d) => !d.data()['deletedAt']);

        setCourse({
          id: courseId,
          title: [courseData.code, courseData.name].filter(Boolean).join(' · ') || courseId,
        });
        setStored(stored);
        setForm(stored ? formFromCap(stored) : emptyForm());
        setDraft(tpl ? buildCcdDraft(tpl.data() as unknown as CourseTemplateProps) : null);
      } catch (e) {
        if (!cancelled) setError(errorText(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [currentTenantId, courseIdParam, courseTemplateId, t]);

  // Live TCU bands per selected level — recomputed every render (GradingSection pattern).
  const bands = useMemo(() => {
    const out: Partial<
      Record<
        AcademicLevel,
        { totalHours: number; minTcu: number; maxTcu: number; suggestedTcu: number }
      >
    > = {};
    for (const level of form.academicLevels) {
      out[level] = computeTcu(form.timeAllocation[level], level, TMAI_CAP_FRAMEWORK_2022_03);
    }
    return out;
  }, [form.academicLevels, form.timeAllocation]);

  const tcuIssues = useMemo(() => {
    const issues: Partial<Record<AcademicLevel, boolean>> = {};
    for (const level of form.academicLevels) {
      const declared = Number(form.tcu[level]);
      if (!form.tcu[level] || !Number.isFinite(declared)) continue;
      issues[level] = !computeTcu(
        form.timeAllocation[level],
        level,
        TMAI_CAP_FRAMEWORK_2022_03,
        declared,
      ).isConsistent;
    }
    return issues;
  }, [form.academicLevels, form.timeAllocation, form.tcu]);

  // TMAI instructional-language rule: some audience language differs from the
  // instructional one → a translation declaration (j ∈ {yes,no}) is required.
  const languageRuleTriggers = useMemo(
    () =>
      !!form.instructionalLanguage &&
      form.targetAudienceLanguages.some((l) => l !== form.instructionalLanguage),
    [form.instructionalLanguage, form.targetAudienceLanguages],
  );

  const setAllocation = useCallback(
    (level: AcademicLevel, row: keyof TimeAllocation, value: string): void => {
      const hours = Math.max(0, Number(value) || 0);
      setForm((f) => ({
        ...f,
        timeAllocation: {
          ...f.timeAllocation,
          [level]: { ...f.timeAllocation[level], [row]: hours },
        },
      }));
    },
    [],
  );

  const toggleLevel = (level: AcademicLevel): void => {
    setForm((f) => {
      const has = f.academicLevels.includes(level);
      if (has && f.academicLevels.length === 1) return f; // keep at least one
      return {
        ...f,
        academicLevels: has
          ? f.academicLevels.filter((l) => l !== level)
          : [...f.academicLevels, level],
      };
    });
  };

  const confirm = async (): Promise<void> => {
    if (!currentTenantId || !course) return;
    setSaving(true);
    setError(null);
    try {
      const clos = {
        undergraduate: form.closUndergraduate
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
        graduate: form.closGraduate
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
      };
      const tcu: Partial<Record<AcademicLevel, number>> = {};
      const timeAllocation: Partial<Record<AcademicLevel, TimeAllocation>> = {};
      for (const level of form.academicLevels) {
        tcu[level] = Number(form.tcu[level]);
        timeAllocation[level] = form.timeAllocation[level];
      }
      const res = await capApi.upsertCcd({
        tenantId: currentTenantId,
        courseId: course.id,
        ccd: {
          academicLevels: form.academicLevels,
          timeAllocation,
          tcu,
          ...(form.courseNumber.trim() ? { courseNumber: form.courseNumber.trim() } : {}),
          ...(form.capSubjectArea ? { capSubjectArea: form.capSubjectArea } : {}),
          ...(form.description.trim() ? { description: form.description.trim() } : {}),
          ...(form.deliveryFormat ? { deliveryFormat: form.deliveryFormat } : {}),
          ...(form.targetAudience.trim() ? { targetAudience: form.targetAudience.trim() } : {}),
          ...(form.targetAudienceLanguages.length > 0
            ? { targetAudienceLanguages: form.targetAudienceLanguages }
            : {}),
          ...(form.instructionalLanguage
            ? { instructionalLanguage: form.instructionalLanguage }
            : {}),
          // j: yes/no when the rule triggers; explicit 'na' when i and h are set
          // and all coincide (matches the TMAI example); omitted otherwise.
          ...(languageRuleTriggers && form.translationProvided
            ? { translationProvided: form.translationProvided }
            : !languageRuleTriggers &&
                form.instructionalLanguage &&
                form.targetAudienceLanguages.length > 0
              ? { translationProvided: 'na' as const }
              : {}),
          ...(form.externalAccreditation.trim()
            ? { externalAccreditation: form.externalAccreditation.trim() }
            : {}),
          ...(clos.undergraduate.length > 0 || clos.graduate.length > 0 ? { clos } : {}),
        },
      });
      setSavedVersion(res.version);
      // What came back IS the stored card, so the frozen-TCU chip and the
      // prefill banner stop lying without a reload.
      setStored(res.cap);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDisabled =
    saving ||
    form.academicLevels.some(
      (level) => !form.tcu[level] || !Number.isFinite(Number(form.tcu[level])),
    ) ||
    Object.values(tcuIssues).some(Boolean) ||
    // Rule triggers but no translation declaration → server would reject.
    (languageRuleTriggers && !form.translationProvided);

  // El CCD es dato de acreditación: lo escriben admin, Secretaría y el decano.
  // Sin esta guarda un profesor llenaba el formulario entero y se enteraba recién
  // al guardar, con un permission-denied del callable.
  if (!isAdmin && !hasRole('registrar') && !hasRole('academicDean')) {
    return <Navigate to="/cap" replace />;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box maxWidth={1100} mx="auto" px={2} py={3}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Button startIcon={<ArrowBackRounded />} onClick={() => navigate('/plantillas-syllabus')}>
          {t('ccd.back')}
        </Button>
      </Stack>
      <Typography variant="h5" fontWeight={700}>
        {t('ccd.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t('ccd.subtitle', { course: course?.title ?? courseIdParam ?? courseTemplateId })}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Prefill banner only while the CCD hasn't been confirmed yet — once a
          card exists, re-suggesting the draft is noise (PR3 boy-scout fix). */}
      {draft && !draftApplied && !stored && (
        <Alert
          severity="info"
          icon={<AutoAwesomeRounded />}
          sx={{ mb: 2 }}
          action={
            <Button
              size="small"
              onClick={() => {
                setForm((f) => applyDraft(f, draft));
                setDraftApplied(true);
              }}
            >
              {t('ccd.prefill.apply')}
            </Button>
          }
        >
          {draft.sourceImportedFrom
            ? t('ccd.prefill.bannerImported', { file: basename(draft.sourceImportedFrom) })
            : t('ccd.prefill.bannerTemplate')}{' '}
          {t('ccd.prefill.nothingSaved')}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        {/* Left column — identity, description, CLOs */}
        <Stack flex={2} spacing={2} minWidth={320} width="100%">
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={t('ccd.fields.courseNumber')}
                value={form.courseNumber}
                onChange={(e) => setForm((f) => ({ ...f, courseNumber: e.target.value }))}
                sx={{ flex: 1 }}
              />
              <TextField
                label={t('ccd.fields.title')}
                value={course?.title ?? ''}
                disabled
                sx={{ flex: 2 }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={2}>
              <TextField
                select
                label={t('ccd.fields.subjectArea')}
                value={form.capSubjectArea}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    capSubjectArea: e.target.value as SubjectAreaCode | '',
                  }))
                }
                helperText={t('ccd.fields.subjectAreaHelp')}
                sx={{ flex: 1, minWidth: 220 }}
              >
                <MenuItem value="">{t('ccd.fields.subjectAreaNone')}</MenuItem>
                {SUBJECT_AREAS.map((code) => (
                  <MenuItem key={code} value={code}>
                    {t(`subjectAreas.${code}`)}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('ccd.fields.academicLevels')}
                </Typography>
                <Stack direction="row">
                  {LEVELS.map((level) => (
                    <FormControlLabel
                      key={level}
                      control={
                        <Checkbox
                          checked={form.academicLevels.includes(level)}
                          onChange={() => toggleLevel(level)}
                        />
                      }
                      label={t(`levels.${level}`)}
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
            <TextField
              label={t('ccd.fields.description')}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              multiline
              minRows={3}
              fullWidth
              sx={{ mt: 2 }}
            />
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" mb={1}>
              {t('ccd.fields.clos')}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={t('ccd.fields.closUndergraduate')}
                value={form.closUndergraduate}
                onChange={(e) => setForm((f) => ({ ...f, closUndergraduate: e.target.value }))}
                helperText={t('ccd.fields.closHelp')}
                multiline
                minRows={4}
                sx={{ flex: 1 }}
              />
              <TextField
                label={t('ccd.fields.closGraduate')}
                value={form.closGraduate}
                onChange={(e) => setForm((f) => ({ ...f, closGraduate: e.target.value }))}
                helperText={t('ccd.fields.closHelp')}
                multiline
                minRows={4}
                sx={{ flex: 1 }}
              />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label={t('ccd.fields.deliveryFormat')}
                value={form.deliveryFormat}
                onChange={(e) => setForm((f) => ({ ...f, deliveryFormat: e.target.value }))}
                sx={{ flex: 1, minWidth: 200 }}
              >
                <MenuItem value="">—</MenuItem>
                {DELIVERY_FORMATS.map((fmt) => (
                  <MenuItem key={fmt} value={fmt}>
                    {t(`deliveryFormats.${fmt}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t('ccd.fields.externalAccreditation')}
                value={form.externalAccreditation}
                onChange={(e) => setForm((f) => ({ ...f, externalAccreditation: e.target.value }))}
                sx={{ flex: 1 }}
              />
            </Stack>
            <TextField
              label={t('ccd.fields.targetAudience')}
              value={form.targetAudience}
              onChange={(e) => setForm((f) => ({ ...f, targetAudience: e.target.value }))}
              helperText={t('ccd.fields.targetAudienceHelp')}
              multiline
              minRows={2}
              fullWidth
              sx={{ mt: 2 }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={2}>
              <TextField
                select
                label={t('ccd.fields.targetAudienceLanguages')}
                value={form.targetAudienceLanguages}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    targetAudienceLanguages:
                      typeof e.target.value === 'string'
                        ? e.target.value.split(',')
                        : (e.target.value as unknown as string[]),
                  }))
                }
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) =>
                    (selected as string[]).map((c) => t(`languages.${c}`)).join(', '),
                }}
                helperText={t('ccd.fields.targetAudienceLanguagesHelp')}
                sx={{ flex: 1, minWidth: 220 }}
              >
                {CAP_LANGUAGE_CODES.map((code) => (
                  <MenuItem key={code} value={code}>
                    {t(`languages.${code}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t('ccd.fields.instructionalLanguage')}
                value={form.instructionalLanguage}
                onChange={(e) => setForm((f) => ({ ...f, instructionalLanguage: e.target.value }))}
                sx={{ flex: 1, minWidth: 200 }}
              >
                <MenuItem value="">—</MenuItem>
                {CAP_LANGUAGE_CODES.map((code) => (
                  <MenuItem key={code} value={code}>
                    {t(`languages.${code}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            {languageRuleTriggers && (
              <TextField
                select
                label={t('ccd.fields.translationProvided')}
                value={form.translationProvided}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    translationProvided: e.target.value as '' | 'yes' | 'no',
                  }))
                }
                error={!form.translationProvided}
                helperText={t('ccd.fields.translationProvidedHelp')}
                sx={{ mt: 2, minWidth: 240 }}
              >
                <MenuItem value="yes">{t('ccd.fields.translationYes')}</MenuItem>
                <MenuItem value="no">{t('ccd.fields.translationNo')}</MenuItem>
              </TextField>
            )}
          </Paper>
        </Stack>

        {/* Right column — time allocation + TCU + confirm */}
        <Stack flex={1} spacing={2} minWidth={280} width="100%">
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" mb={1}>
              {t('ccd.allocation.title')}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('ccd.allocation.activity')}</TableCell>
                  {form.academicLevels.map((level) => (
                    <TableCell key={level} align="right">
                      {t(`levels.short.${level}`)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {ALLOCATION_ROWS.map((row) => (
                  <TableRow key={row}>
                    <TableCell>{t(`ccd.allocation.rows.${row}`)}</TableCell>
                    {form.academicLevels.map((level) => (
                      <TableCell key={level} align="right" sx={{ width: 90 }}>
                        <TextField
                          value={form.timeAllocation[level][row] || ''}
                          onChange={(e) => setAllocation(level, row, e.target.value)}
                          size="small"
                          type="number"
                          inputProps={{ min: 0, style: { textAlign: 'right' } }}
                          variant="standard"
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>{t('ccd.allocation.total')}</TableCell>
                  {form.academicLevels.map((level) => (
                    <TableCell key={level} align="right" sx={{ fontWeight: 700 }}>
                      {bands[level]?.totalHours ?? 0}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
            <Divider sx={{ my: 1.5 }} />
            {form.academicLevels.map((level) => {
              const band = bands[level];
              if (!band) return null;
              const suggestedIsInteger = Number.isInteger(band.suggestedTcu);
              return (
                <Box key={level} mb={1.5}>
                  <Typography variant="caption" color="text.secondary">
                    {t('ccd.tcu.band', {
                      level: t(`levels.short.${level}`),
                      min: band.minTcu,
                      max: band.maxTcu,
                    })}{' '}
                    {suggestedIsInteger
                      ? t('ccd.tcu.suggested', { tcu: band.suggestedTcu })
                      : t('ccd.tcu.suggestedApprox', { tcu: band.suggestedTcu })}
                  </Typography>
                  <TextField
                    label={t('ccd.tcu.declared', { level: t(`levels.short.${level}`) })}
                    value={form.tcu[level]}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, tcu: { ...f.tcu, [level]: e.target.value } }))
                    }
                    size="small"
                    type="number"
                    fullWidth
                    sx={{ mt: 0.5 }}
                    error={!!tcuIssues[level]}
                    helperText={tcuIssues[level] ? t('ccd.tcu.inconsistent') : ' '}
                  />
                </Box>
              );
            })}
            {stored?.tcu && (
              <Chip
                size="small"
                icon={<CheckCircleRounded />}
                label={t('ccd.tcu.frozen', {
                  tcu: Object.entries(stored.tcu)
                    .map(([lvl, v]) => `${t(`levels.short.${lvl as AcademicLevel}`)}: ${v}`)
                    .join(' · '),
                })}
                color="success"
                variant="outlined"
              />
            )}
          </Paper>

          <Button
            variant="contained"
            size="large"
            onClick={() => void confirm()}
            disabled={confirmDisabled}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            {t('ccd.confirm')}
          </Button>
        </Stack>
      </Stack>

      <Snackbar
        open={savedVersion !== null}
        autoHideDuration={4000}
        onClose={() => setSavedVersion(null)}
        message={t('ccd.saved', { version: savedVersion ?? 0 })}
      />
    </Box>
  );
}
